import React, { useState, useEffect, useCallback } from 'react';
import { Header } from './components/Header.tsx';
import { RSVPForm } from './components/RSVPForm.tsx';
import { Dashboard } from './components/Dashboard.tsx';
import { Footer } from './components/Footer.tsx';
import { ThankYouPage } from './components/ThankYouPage.tsx';
import { PasswordPrompt } from './components/PasswordPrompt.tsx';
import { fetchRsvps } from './services/apiService.ts'; // Import fetchRsvps from apiService
import { RSVP } from './types.ts';
import { DASHBOARD_PASSWORD } from './constants.ts';

type ViewState = 'form' | 'dashboard';

function App() {
  const [rsvps, setRsvps] = useState<RSVP[]>([]);
  const [currentView, setCurrentView] = useState<ViewState>(() => {
    return window.location.hash === '#/dashboard' ? 'dashboard' : 'form';
  });
  const [showThankYouScreen, setShowThankYouScreen] = useState<boolean>(false);
  const [submittedRsvpData, setSubmittedRsvpData] = useState<RSVP | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null); // New state for global errors
  const [isLoadingRsvps, setIsLoadingRsvps] = useState<boolean>(false); // New state for RSVP loading

  const loadRsvps = useCallback(async () => {
    setGlobalError(null); // Clear previous global errors
    setIsLoadingRsvps(true); // Set loading to true before fetching
    try {
      const data = await fetchRsvps();
      setRsvps(data);
    } catch (error: any) {
      console.error("Failed to load RSVPs:", error);
      setGlobalError(error.message || 'Failed to load RSVPs from the server.');
    } finally {
      setIsLoadingRsvps(false); // Set loading to false after fetching (success or error)
    }
  }, []);

  useEffect(() => {
    loadRsvps();

    const handleHashChange = () => {
      // If hash changes, hide thank you screen, clear submitted data, and update view
      setShowThankYouScreen(false);
      setSubmittedRsvpData(null);
      setGlobalError(null); // Clear global errors on view change
      
      const newView = window.location.hash === '#/dashboard' ? 'dashboard' : 'form';
      setCurrentView(newView);

      // If navigating away from the dashboard, reset authentication state
      if (newView !== 'dashboard') {
        setIsAuthenticated(false);
        setAuthError(null);
      } else {
        // If navigating to dashboard, re-fetch RSVPs
        loadRsvps();
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [loadRsvps]);

  const handleRSVPComplete = useCallback((rsvpData: RSVP) => {
    loadRsvps(); // Reload RSVPs from server after new submission
    setSubmittedRsvpData(rsvpData);
    setShowThankYouScreen(true);
  }, [loadRsvps]);

  const handleGoBackToForm = useCallback(() => {
    setShowThankYouScreen(false);
    setSubmittedRsvpData(null);
    window.location.hash = '';
    setCurrentView('form');
  }, []);

  const handleAuthenticateDashboard = useCallback((password: string) => {
    if (password === DASHBOARD_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError(null);
      loadRsvps(); // Load RSVPs once authenticated
    } else {
      setAuthError('Incorrect password. Please try again.');
      setIsAuthenticated(false);
    }
  }, [loadRsvps]);

  const handleGoToRegistration = useCallback(() => {
    setShowThankYouScreen(false);
    setSubmittedRsvpData(null);
    setIsAuthenticated(false); // Ensure dashboard is no longer authenticated
    setAuthError(null);
    window.location.hash = ''; // Clear the hash to go back to the root path
    setCurrentView('form');
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center bg-primary-bg text-text-white p-4 relative font-montserrat">
      <Header />
      <main className="flex-grow w-full max-w-4xl p-4 md:p-8 bg-primary-bg border-2 border-accent-gold shadow-gold-xl rounded-lg my-8 relative flex flex-col items-center">
        {globalError && (
          <div className="bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded relative mb-4 w-full" role="alert">
            <strong className="font-bold">Error!</strong>
            <span className="block sm:inline ml-2">{globalError}</span>
          </div>
        )}
        {showThankYouScreen && submittedRsvpData ? (
          <ThankYouPage rsvpData={submittedRsvpData} onGoBackToForm={handleGoBackToForm} />
        ) : currentView === 'form' ? (
          <RSVPForm onRSVPComplete={handleRSVPComplete} />
        ) : ( // currentView === 'dashboard'
          isAuthenticated ? (
            isLoadingRsvps ? (
              <div className="flex flex-col items-center justify-center p-8 text-text-white">
                <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-accent-gold mb-4"></div>
                <p className="text-lg">Loading RSVPs...</p>
              </div>
            ) : (
              <Dashboard rsvps={rsvps} onGoToRegistration={handleGoToRegistration} />
            )
          ) : (
            <PasswordPrompt onAuthenticate={handleAuthenticateDashboard} error={authError} onGoToRegistration={handleGoToRegistration} />
          )
        )}
      </main>
      <Footer />
    </div>
  );
}

export default App;